import { useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiClient } from '@/lib/apiClient';
import type { ApiResponse } from '@/types/api';
import { USE_MOCKS } from '@/lib/mockConfig';
import {
  initialChannels,
  initialAiSettings,
  initialBusinessHours,
  initialQuickReplies,
  initialRules,
  type ChannelAutomation,
  type AiResponseSettings,
  type BusinessHoursSettings,
  type QuickReply,
  type AutomationRule,
} from '../mockAutomations';

export interface AutomationConfig {
  channels: ChannelAutomation[];
  aiSettings: AiResponseSettings;
  businessHours: BusinessHoursSettings;
  quickReplies: QuickReply[];
  rules: AutomationRule[];
}

const mockConfig = (): AutomationConfig => ({
  channels: initialChannels,
  aiSettings: initialAiSettings,
  businessHours: initialBusinessHours,
  quickReplies: initialQuickReplies,
  rules: initialRules,
});

/**
 * Loads/persists the Automations control surface. Everything lives in the shop
 * config's flexible `settingsJson.automations` blob, so no schema change is
 * needed. Falls back to the mock defaults when there's no config yet (empty DB).
 * `save` merges into the existing settingsJson so other keys aren't clobbered.
 */
export function useAutomationConfig() {
  const rawSettingsRef = useRef<Record<string, any>>({});

  const query = useQuery({
    queryKey: ['automation-config'],
    queryFn: () =>
      apiClient.get<ApiResponse<any>>('/api/chatbot/config')
        .then((res) => {
          const cfg = res.data.data;
          rawSettingsRef.current = cfg?.settingsJson || {};
          const stored = cfg?.settingsJson?.automations as Partial<AutomationConfig> | undefined;
          if (USE_MOCKS && !stored) return mockConfig();
          return { ...mockConfig(), ...(stored || {}) };
        })
        .catch((err) => {
          if (USE_MOCKS) return mockConfig();
          throw err;
        }),
    staleTime: 60 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: (config: AutomationConfig) =>
      apiClient.patch('/api/chatbot/config', {
        settingsJson: { ...rawSettingsRef.current, automations: config },
      }),
    // Silent: an empty DB has no ShopConfig yet, so PATCH returns 404 (handled
    // gracefully now that async errors are forwarded, not crashing).
    onError: () => {},
  });

  return {
    // Starter defaults (generic templates) until the real config arrives, or for a new tenant.
    config: query.data ?? mockConfig(),
    isLoading: query.isLoading,
    // After a failed load the screen shows defaults, not the tenant's real rules; saving them
    // would overwrite the real ones. Refuse, and say so.
    save: (config: AutomationConfig) => {
      if (query.isError) {
        toast.error("Your automation settings didn't load, so nothing was saved. Refresh and try again.");
        return;
      }
      saveMutation.mutate(config);
    },
  };
}
